'use client';

// useChat - Consumer chat hook with SSE streaming
// Story 4.3: Real-Time Bot Responses
//
// Differences from creator's useChat:
// - No TanStack Query (localStorage is source of truth for consumer)
// - Message validation: max 500 chars (consumer limit)
// - Simpler storage key: boty_consumer_chat_{botId}
// - Import Message type from @boty/ui

import { useState, useCallback, useRef, useEffect } from 'react';
import type { Message } from '@boty/ui';

// Storage key for conversation persistence
const CHAT_STORAGE_KEY = 'boty_consumer_chat';

function saveMessagesToStorage(botId: string, messages: Message[]): void {
  if (typeof window === 'undefined') return;
  try {
    const key = `${CHAT_STORAGE_KEY}_${botId}`;
    localStorage.setItem(key, JSON.stringify(messages));
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

function loadMessagesFromStorage(botId: string): Message[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const key = `${CHAT_STORAGE_KEY}_${botId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function clearMessagesFromStorage(botId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const key = `${CHAT_STORAGE_KEY}_${botId}`;
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
}

export interface UseChatOptions {
  botId: string;
  sessionId: string;
  enabled?: boolean;
  isCreatorPreview?: boolean;
}

export interface UseChatReturn {
  messages: Message[];
  isLoading: boolean;
  error: Error | null;
  sendMessage: (content: string) => void;
  clearChat: () => void;
}

export function useChat({
  botId,
  sessionId,
  enabled = true,
  isCreatorPreview = false,
}: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const streamingMessageIdRef = useRef<string | null>(null);
  const hasInitialized = useRef(false);

  // Initialize messages from localStorage on mount (one-time only)
  useEffect(() => {
    if (!enabled || !botId || !sessionId || hasInitialized.current) return;
    hasInitialized.current = true;

    const storedMessages = loadMessagesFromStorage(botId);
    if (storedMessages && storedMessages.length > 0) {
      setMessages(storedMessages);
    }
  }, [botId, sessionId, enabled]);

  // Save messages to storage after streaming completes (not during)
  useEffect(() => {
    if (messages.length > 0 && !isLoading) {
      saveMessagesToStorage(botId, messages);
    }
  }, [messages, botId, isLoading]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading || !enabled) return;

      setError(null);
      setIsLoading(true);

      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: content.trim(),
        createdAt: new Date().toISOString(),
      };

      const assistantMessageId = crypto.randomUUID();
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
        isStreaming: true,
      };

      streamingMessageIdRef.current = assistantMessageId;

      // Add both messages optimistically
      setMessages((prev) => [...prev, userMessage, assistantMessage]);

      try {
        const response = await fetch(`/api/bots/${botId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: content.trim(), sessionId, isCreatorPreview: isCreatorPreview || undefined }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error?.message || 'Failed to send message');
        }

        // Read SSE stream
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          // Keep the last incomplete line in buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;

            const data = line.slice(6); // Remove 'data: ' prefix
            if (data === '[DONE]') {
              // Stream complete
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId ? { ...msg, isStreaming: false } : msg
                )
              );
              break;
            }

            let parsed: Record<string, unknown>;
            try {
              parsed = JSON.parse(data);
            } catch {
              // Ignore JSON parse errors for individual chunks
              continue;
            }

            // Check if it's an error event from the server
            if ('error' in parsed) {
              const errorObj = parsed.error as { message: string };
              throw new Error(errorObj.message);
            }

            // Update assistant message with new content
            if ('content' in parsed) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: msg.content + (parsed.content as string) }
                    : msg
                )
              );
            }
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        setError(new Error(errorMessage));

        // Remove the placeholder assistant message on error
        setMessages((prev) => prev.filter((msg) => msg.id !== assistantMessageId));
      } finally {
        setIsLoading(false);
        streamingMessageIdRef.current = null;
      }
    },
    [botId, sessionId, isLoading, enabled, isCreatorPreview]
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    clearMessagesFromStorage(botId);
  }, [botId]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearChat,
  };
}

export default useChat;
